import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, Download, CheckCircle, XCircle, HelpCircle,
  Calendar, Car, Clock, AlertTriangle, Wrench
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReportPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [steps, setSteps] = useState([]);
  const [vehicle, setVehicle] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [reportPdfUrl, setReportPdfUrl] = useState(null);

  useEffect(() => {
    fetchSessionData();
  }, [sessionId]);

  const fetchSessionData = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch session details
      const sessionResponse = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(sessionResponse.data);

      // Fetch steps
      const stepsResponse = await axios.get(`${API}/sessions/${sessionId}/steps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSteps(stepsResponse.data || []);

      // Fetch vehicle data
      if (sessionResponse.data.vehicle_id) {
        const vehicleResponse = await axios.get(`${API}/vehicles/${sessionResponse.data.vehicle_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVehicle(vehicleResponse.data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Session-Daten');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/sessions/${sessionId}/report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pdfUrl = `${BACKEND_URL}${response.data.pdf_url}`;
      setReportPdfUrl(pdfUrl);
      toast.success('PDF-Report wurde erstellt! Klicken Sie auf "PDF herunterladen"');
    } catch (error) {
      toast.error('Fehler beim Erstellen des PDF-Reports');
      console.error(error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'good':
        return <CheckCircle className="w-5 h-5" style={{ color: '#22C55E' }} />;
      case 'bad':
        return <XCircle className="w-5 h-5" style={{ color: '#EF4444' }} />;
      case 'unclear':
        return <HelpCircle className="w-5 h-5" style={{ color: '#F59E0B' }} />;
      default:
        return <HelpCircle className="w-5 h-5" style={{ color: '#94A3B8' }} />;
    }
  };

  const getResultBadge = (result) => {
    const colors = {
      good: { bg: '#22C55E20', text: '#22C55E' },
      bad: { bg: '#EF444420', text: '#EF4444' },
      unclear: { bg: '#F59E0B20', text: '#F59E0B' }
    };
    const color = colors[result] || { bg: '#94A3B820', text: '#94A3B8' };
    const labels = {
      good: 'Gut',
      bad: 'Schlecht',
      unclear: 'Unklar'
    };
    
    return (
      <span
        className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
        style={{ backgroundColor: color.bg, color: color.text }}
      >
        {getResultIcon(result)}
        {labels[result] || 'Unbekannt'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center h-64">
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: '#F59E0B' }} />
            <p style={{ color: '#E6EDF3' }}>Session nicht gefunden</p>
            <Button
              onClick={() => navigate('/history')}
              className="mt-4"
              style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
            >
              Zurück zur Historie
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Find final diagnosis
  const finalDiagnosisStep = steps.find(step => step.result === 'bad');

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
              Diagnose-Report
            </h2>
            <p style={{ color: '#94A3B8' }}>
              Session vom {new Date(session.created_at).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div className="flex gap-3">
            {!reportPdfUrl ? (
              <Button
                onClick={handleGeneratePdf}
                disabled={generatingPdf}
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                <Download className="w-4 h-4 mr-2" />
                {generatingPdf ? 'Erstelle PDF...' : 'PDF Report generieren'}
              </Button>
            ) : (
              <Button 
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(reportPdfUrl, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Bericht_${sessionId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (error) {
                    toast.error('Fehler beim Herunterladen');
                    console.error(error);
                  }
                }}
                style={{ backgroundColor: '#22C55E', color: '#ffffff' }}
              >
                <Download className="w-4 h-4 mr-2" />
                ✓ PDF herunterladen
              </Button>
            )}
            <Button
              onClick={() => navigate('/history')}
              variant="outline"
              style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
            >
              Zurück
            </Button>
          </div>
        </div>

        {/* Vehicle & Session Info */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                <Car className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                Fahrzeugdaten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {vehicle ? (
                <>
                  <div className="flex justify-between">
                    <span style={{ color: '#94A3B8' }}>Marke:</span>
                    <span style={{ color: '#E6EDF3' }}>{vehicle.make}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#94A3B8' }}>Modell:</span>
                    <span style={{ color: '#E6EDF3' }}>{vehicle.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#94A3B8' }}>Baujahr:</span>
                    <span style={{ color: '#E6EDF3' }}>{vehicle.year}</span>
                  </div>
                  {vehicle.vin && (
                    <div className="flex justify-between">
                      <span style={{ color: '#94A3B8' }}>FIN:</span>
                      <span style={{ color: '#E6EDF3' }}>{vehicle.vin}</span>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ color: '#94A3B8' }}>Keine Fahrzeugdaten verfügbar</p>
              )}
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                <FileText className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                Session-Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between items-center">
                <span style={{ color: '#94A3B8' }}>Status:</span>
                <span
                  className="px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: session.status === 'done' ? '#22C55E20' : '#F59E0B20',
                    color: session.status === 'done' ? '#22C55E' : '#F59E0B'
                  }}
                >
                  {session.status === 'done' ? 'Abgeschlossen' : 'In Bearbeitung'}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>Schritte:</span>
                <span style={{ color: '#E6EDF3' }}>{steps.length}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>Sprache:</span>
                <span style={{ color: '#E6EDF3' }}>{session.locale === 'de' ? 'Deutsch' : 'English'}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Symptom Description */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Symptombeschreibung</CardTitle>
          </CardHeader>
          <CardContent>
            <p style={{ color: '#E6EDF3' }}>{session.symptom_text}</p>
          </CardContent>
        </Card>

        {/* Final Diagnosis */}
        {finalDiagnosisStep && (
          <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#EF4444' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#EF4444' }}>
                <AlertTriangle className="w-5 h-5" />
                Diagnose-Ergebnis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>Identifiziertes Problem:</h4>
                <p style={{ color: '#E6EDF3' }}>
                  {finalDiagnosisStep.structured_data?.prüfschritt || finalDiagnosisStep.instruction}
                </p>
              </div>
              {finalDiagnosisStep.structured_data?.erklärung && (
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>Erklärung:</h4>
                  <p style={{ color: '#94A3B8' }}>{finalDiagnosisStep.structured_data.erklärung}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Diagnostic Steps Timeline */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <Wrench className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Durchgeführte Diagnoseschritte
            </CardTitle>
          </CardHeader>
          <CardContent>
            {steps.length === 0 ? (
              <p style={{ color: '#94A3B8' }}>Noch keine Schritte durchgeführt</p>
            ) : (
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div key={step.id} className="relative">
                    {/* Timeline connector */}
                    {index < steps.length - 1 && (
                      <div
                        className="absolute left-6 top-12 bottom-0 w-0.5"
                        style={{ backgroundColor: '#1F2937' }}
                      />
                    )}
                    
                    <div className="flex gap-4">
                      {/* Step number */}
                      <div
                        className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold"
                        style={{ backgroundColor: '#1F2937', color: '#7CD4FD' }}
                      >
                        {index + 1}
                      </div>

                      {/* Step content */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold" style={{ color: '#E6EDF3' }}>
                            {step.instruction || step.structured_data?.prüfschritt || 'Prüfschritt'}
                          </h4>
                          {getResultBadge(step.result)}
                        </div>

                        {step.structured_data?.erklärung && (
                          <p className="mb-2" style={{ color: '#94A3B8' }}>
                            {step.structured_data.erklärung}
                          </p>
                        )}

                        {step.actual_value && (
                          <div className="mb-2 p-3 rounded" style={{ backgroundColor: '#0B0F14' }}>
                            <p className="text-sm font-semibold mb-1" style={{ color: '#10B981' }}>
                              📊 Gemessener Wert:
                            </p>
                            <p className="text-sm" style={{ color: '#E6EDF3' }}>
                              {step.actual_value}
                            </p>
                          </div>
                        )}

                        {step.assessment_message && (
                          <div className="mb-2 p-3 rounded" style={{ 
                            backgroundColor: '#0B0F14',
                            borderLeft: `3px solid ${
                              step.assessment_status === 'ok' ? '#10B981' :
                              step.assessment_status === 'warning' ? '#F59E0B' :
                              step.assessment_status === 'critical' ? '#EF4444' :
                              '#6B7280'
                            }`
                          }}>
                            <p className="text-sm font-semibold mb-1" style={{ 
                              color: step.assessment_status === 'ok' ? '#10B981' :
                                     step.assessment_status === 'warning' ? '#F59E0B' :
                                     step.assessment_status === 'critical' ? '#EF4444' :
                                     '#E6EDF3'
                            }}>
                              {step.assessment_status === 'ok' ? '✓' :
                               step.assessment_status === 'warning' ? '⚠' :
                               step.assessment_status === 'critical' ? '✗' : '•'} Bauteilbeurteilung:
                            </p>
                            <p className="text-sm" style={{ color: '#E6EDF3' }}>
                              {step.assessment_message}
                            </p>
                          </div>
                        )}

                        {step.note && (
                          <div className="mb-2 p-3 rounded" style={{ backgroundColor: '#0B0F14' }}>
                            <p className="text-sm font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                              📝 Notiz:
                            </p>
                            <p className="text-sm" style={{ color: '#E6EDF3' }}>
                              {step.note}
                            </p>
                          </div>
                        )}

                        {step.structured_data?.lokalisierung && (
                          <div className="mb-2 p-3 rounded" style={{ backgroundColor: '#0B0F14' }}>
                            <p className="text-sm font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                              Lokalisierung:
                            </p>
                            <p className="text-sm" style={{ color: '#E6EDF3' }}>
                              {step.structured_data.lokalisierung}
                            </p>
                          </div>
                        )}

                        {step.user_feedback && (
                          <div className="mt-2 p-3 rounded" style={{ backgroundColor: '#0B0F14' }}>
                            <p className="text-sm font-semibold mb-1" style={{ color: '#F59E0B' }}>
                              Nutzer-Feedback:
                            </p>
                            <p className="text-sm" style={{ color: '#E6EDF3' }}>{step.user_feedback}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
